import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from '@cupoui/core/services/http.service';
import { CaseDocumentAdapter } from '@cupoui/case/components/case-summary/models/case-document';
import { SERVER_API_URL } from '../../app.constants';
import { CaseDocumentTagEnum, ICaseDocumentTag } from '@cupoui/shared/model/case-document';
import { AccountRoleService } from '@cupoui/core/auth/account-role.service';
import { NGXLogger } from 'ngx-logger';
import { BusinessAreaEnum } from '@cupoui/shared/model/enumerations/business-area.enum';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  constructor(
    private httpService: HttpService,
    private caseDocumentAdapter: CaseDocumentAdapter,
    private logger: NGXLogger,

    private accountRoleService: AccountRoleService
  ) {}

  public getTravelDocument(documentId: string): Observable<Blob> {
    return this.getDocumentUsingPath(documentId, '/services/documentservice/api/documents/data/');
  }

  public getDocumentByForBusinessArea(documentId: string, businessArea: BusinessAreaEnum): Observable<Blob> {
    if (businessArea === BusinessAreaEnum.Mobility) {
      return this.getDocumentUsingPath(documentId, '/services/axapta/api/invoices/pdf/');
    } else {
      return this.getTravelDocument(documentId);
    }
  }

  public getDocumentTags(documentId: string): Observable<ICaseDocumentTag[]> {
    return this.httpService.get<string[]>(`/services/casemanagement/api/documents/tags/${documentId}`).pipe(
      map((items: string[]) => {
        const arr: ICaseDocumentTag[] = [];
        items.forEach(item => arr.push({ name: item }));
        return arr;
      })
    );
  }

  public getDocumentUrl(documentId: string): string {
    return `${SERVER_API_URL}/services/documentservice/api/documents/data/${documentId}`;
  }

  public getTransformWordToPdf(documentId: string): Observable<Blob> {
    return this.httpService.getDocument(`/services/casemanagement/api/documents/getPdf/`, documentId);
  }

  public removePasswordFromPDF(documentId: string, password: string, caseId: number): Observable<string> {
    return this.httpService.get<string>(`/services/casemanagement/api/documents/removePasswordFromPDF/${documentId}/password/${caseId}`);
  }

  private getDocumentUsingPath(documentId: string, path: string): Observable<Blob> {
    return this.httpService.getDocument(path, documentId);
  }

  canOpenDocument(tags: ICaseDocumentTag[]): boolean {
    if (tags.some(tag => this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.MEDICAL_ESCORT_INVOICE)) {
      return false;
    }
    if (
      tags.every(
        tag =>
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.MEDICAL_INFO &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.MEDIF &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.CUSTOMER_INVOICE &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.SUPPLIER_INVOICE &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.ESCORT_TRAVEL_EXPENSES &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.RECEIPT &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.CLAIM_LETTER &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.GOP_VOB &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.GOP_VOB_CANCEL &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.CONSENT &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.RECOVERY_DECISION &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.RECOVERY_NOT_POSSIBLE &&
          this.caseDocumentEnumFromString(tag.name) !== CaseDocumentTagEnum.MEDICAL_PRE_ASSESSMENT
      )
    ) {
      return true;
    }
    if (
      tags.some(
        tag =>
          (this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.MEDICAL_INFO ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.MEDIF ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.INVESTIGATION ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.TRANSLATION ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.MEDICAL_PRE_ASSESSMENT) &&
          !this.accountRoleService.roles.canViewTravelCasesMedicalInformationExtended
      )
    ) {
      return false;
    }
    if (
      tags.some(
        tag =>
          (this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.CUSTOMER_INVOICE ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.SUPPLIER_INVOICE ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.ESCORT_TRAVEL_EXPENSES ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.RECEIPT ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.CLAIM_LETTER) &&
          !this.accountRoleService.roles.canViewTravelCasesFinancialInformation
      )
    ) {
      return false;
    }
    if (
      tags.some(
        tag =>
          (this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.GOP_VOB ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.GOP_VOB_CANCEL ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.CONSENT ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.CONSENT_REJECTED ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.RECOVERY_POA_REQUEST ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.RECOVERY_POA_FIRST_REMINDER ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.RECOVERY_POA_SECOND_REMINDER ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.RECOVERY_POA_SIGNED ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.RECOVERY_DECISION ||
            this.caseDocumentEnumFromString(tag.name) === CaseDocumentTagEnum.RECOVERY_NOT_POSSIBLE) &&
          !this.accountRoleService.roles.canViewTravelCasesAttachmentInformationExtended
      )
    ) {
      return false;
    }
    return true;
  }

  caseDocumentEnumFromString(name: string): CaseDocumentTagEnum {
    let match = (Object.values(CaseDocumentTagEnum) as string[]).indexOf(name);
    if (match < 0) {
      match = (Object.keys(CaseDocumentTagEnum) as string[]).indexOf(name);
    }
    return CaseDocumentTagEnum[Object.keys(CaseDocumentTagEnum)[match]];
  }
}
